import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {AppThunk} from "../store.ts";
import {AxiosError} from "axios";
import {
    ICampaignResponseData, ICampaignHistory, ICampaignUpdateParams,
    ICreateCampaignParamsData,
    ICurrentCampaignResponseData, ICampaignHistoryParams, ICampaignStats
} from "../models/CampaignResponse.ts";
import {CampaignService} from "../services/campaign-service.ts";
import {setCampaignId, setPostId, successToastify} from "../../utils/Utils.ts";
import {getPostTC, postActions} from "./post-reducer.ts";
import {PublicationStatus} from "../../constants/enums.ts";
import {showCustomErrorToast} from "../../utils/customErrorToast.tsx";
import {t} from "i18next";

type  InitialStateType = {
    data: ICampaignResponseData[]
    history: {
        data: ICampaignHistory[]
        isLoading: boolean
    }
    stats: {
        data: ICampaignStats[]
        isLoading: boolean
    }
    newCampaign: ICampaignResponseData | null
    currentCampaign: ICurrentCampaignResponseData | null
    currentChannel: ICampaignHistory | null
    isLoading: boolean

};

const initialState: InitialStateType = {
    data: [],
    history: {
        data: [],
        isLoading: false
    },
    stats: {
        data: [],
        isLoading: false
    },
    newCampaign: null,
    currentCampaign: null,
    currentChannel: null,
    isLoading: false,

}

const slice = createSlice({
    name: "campaign",
    initialState: initialState,
    reducers: {
        setInitData: (state: InitialStateType, action: PayloadAction<ICampaignResponseData[]>) => {
            state.data = action.payload
        },
        setNewCampaign: (state: InitialStateType, action: PayloadAction<ICampaignResponseData>) => {
            state.newCampaign = action.payload
        },
        setCurrentCampaign: (state: InitialStateType, action: PayloadAction<ICurrentCampaignResponseData>) => {
            state.currentCampaign = action.payload
        },
        updateStatusOfCurrentCampaign: (state: InitialStateType, action: PayloadAction<PublicationStatus>) => {
            if (state.currentCampaign) {
                state.currentCampaign.status = action.payload
            }
        },
        setCurrentChannel: (state: InitialStateType, action: PayloadAction<ICampaignHistory>) => {
            state.currentChannel = action.payload
        },
        setCampaignHistory: (state: InitialStateType, action: PayloadAction<ICampaignHistory[]>) => {
            state.history.data = action.payload
        },
        setCampaignStats: (state: InitialStateType, action: PayloadAction<ICampaignStats[]>) => {
            state.stats.data = action.payload
        },
        setCampaignStatsLoading: (state: InitialStateType, action: PayloadAction<boolean>) => {
            state.stats.isLoading = action.payload
        },
        setCampaignHistoryLoading: (state: InitialStateType, action: PayloadAction<boolean>) => {
            state.history.isLoading = action.payload
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },

    },
});
export const campaignReducer = slice.reducer;
export const campaignActions = slice.actions;

export const getCampaignsTC = (id: string): AppThunk<Promise<ICampaignResponseData[] | void>> =>
    async (dispatch) => {
        dispatch(campaignActions.setLoading(true));
        try {
            const res = await CampaignService.fetchCampaigns(id);
            dispatch(campaignActions.setInitData(res.data))
            return res.data
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(err.message, 'main', 'bottom-center')
            throw err;
        } finally {
            dispatch(campaignActions.setLoading(false));
        }
    };

export const createCampaignTC = (data: ICreateCampaignParamsData): AppThunk<Promise<ICampaignResponseData | void>> =>
    async (dispatch) => {
        dispatch(campaignActions.setLoading(true));
        try {
            const res = await CampaignService.createCampaign(data);
            dispatch(campaignActions.setNewCampaign(res.data));
            setCampaignId(res.data.id)
            if (res.data.post_id) {
                setPostId(res.data.post_id);
            }
            successToastify(t('pages.createCampaign.successResponse'), 'bottom-center', 'main');

            return res.data
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(t('pages.createCampaign.errorResponse'), 'main', 'bottom-center')
            throw err;
        } finally {
            dispatch(campaignActions.setLoading(false));
        }
    };

export const setCurrentCampaignTC = (id: string): AppThunk<Promise<ICurrentCampaignResponseData | void>> =>
    async (dispatch) => {
        dispatch(campaignActions.setLoading(true));
        dispatch(postActions.setPostdata(null))
        try {
            const res = await CampaignService.getCampaignByCampaignId(id);
            dispatch(campaignActions.setCurrentCampaign(res.data));

            if (res.data.post_id) {
                await dispatch(getPostTC(res.data.post_id))
                if (res.data.post_id) {
                    setPostId(res.data.post_id);
                }
                dispatch(postActions.changeReloadState(true));
            } else {
                dispatch(postActions.changeReloadState(false));
            }
            return res.data
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(err.message, 'main', 'bottom-center')
            throw err;
        } finally {
            dispatch(campaignActions.setLoading(false));
        }
    };

export const setCampaignHistoryTC = (id: string, params?: ICampaignHistoryParams): AppThunk<Promise<ICampaignHistory[] | void>> =>
    async (dispatch) => {
        dispatch(campaignActions.setCampaignHistoryLoading(true));
        try {
            const res = await CampaignService.fetchCampaignHistory(id, params);
            dispatch(campaignActions.setCampaignHistory(res.data));
            return res.data
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(err.message, 'main', 'bottom-center')
            throw err;
        } finally {
            dispatch(campaignActions.setCampaignHistoryLoading(false));
        }
    };

export const setCampaignStatsTC = (id: string, params?: ICampaignHistoryParams): AppThunk<Promise<ICampaignStats[] | void>> =>
    async (dispatch) => {
        dispatch(campaignActions.setCampaignStatsLoading(true));
        try {
            const res = await CampaignService.fetchCampaignStats(id, params);
            dispatch(campaignActions.setCampaignStats(res.data));
            return res.data
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(err.message, 'main', 'bottom-center')
            throw err;
        } finally {
            dispatch(campaignActions.setCampaignStatsLoading(false));
        }
    };

export const updateCurrentCampaignTC = (id: string, data: ICampaignUpdateParams, successTitle: string, errorTitle: string): AppThunk<Promise<ICurrentCampaignResponseData | void>> =>
    async (dispatch) => {
        // dispatch(campaignActions.setLoading(true));
        try {
            const res = await CampaignService.updateCampaign(id, data);
            dispatch(campaignActions.updateStatusOfCurrentCampaign(res.data.status));
            successToastify(successTitle, 'bottom-center', 'main');

            return res.data
        } catch (err) {
            if (err instanceof AxiosError)
                showCustomErrorToast(errorTitle, 'main', 'bottom-center')
            throw err;
        } finally {
            dispatch(campaignActions.setLoading(false));
        }
    };
